/**
 * Manifest skill-up — feature témoin V2.
 *
 * @remarks
 * Première feature qui exerce le contrat plugin V2 de bout en bout :
 * `centerView` lazy-loadées, `routes` déclarées, `navigation`, `panels`
 * gauche/droite déclarés, `panelRequirements` par contexte.
 *
 * Layout validé par Florian (cf. articles KB SAAS-A-101 / A-102 / A-103) :
 *  - Gauche : "Mon espace" (workspace personnel) + Sessions (liste plate
 *    style chat) — identiques sur les 3 contextes skill-up.
 *  - Droite : Parcours (graph xyflow) + Explications — uniquement sur
 *    SKILL_UP_SESSION.
 *  - HOME et COMPETENCES : pas de sidebar droite (`right.open: false`).
 *
 * Le `CenterTabs` (barre d'onglets centre IDE-like) n'est pas déclaré
 * ici — il est rendu **dans** `LearningSessionView` car le shell ne
 * gère qu'un seul tab actif au centre via `centerView`.
 */

import { FolderTree, GitFork, GraduationCap, Lightbulb, MessagesSquare } from 'lucide-react';
import { CONTEXTS, lazyView, type FeatureManifest, type TabConfig } from '@/app/registry';
import { GRAPH_TAB_ID, LEXIQUE_TAB_ID } from './lib/panel-tabs';
import { skillUpRoutes } from './routes';

// ─── Tabs gauches partagés ──────────────────────────────────────────────
// Identiques sur les 3 contextes skill-up (HOME, SESSION, COMPETENCES) —
// déclarés une fois en module-scope pour éviter la duplication + garder
// la même instance `view` (lazy chunk partagé) entre contextes.

const leftTabs: TabConfig[] = [
  {
    id: 'skill-up-sessions',
    label: 'Sessions',
    icon: MessagesSquare,
    view: lazyView(() => import('./views/SkillUpSessionsView'), 'SkillUpSessionsView'),
  },
  {
    id: 'skill-up-workspace',
    label: 'Mon espace',
    icon: FolderTree,
    view: lazyView(() => import('./views/SkillUpWorkspaceView'), 'SkillUpWorkspaceView'),
  },
];

export const skillUpManifest: FeatureManifest = {
  id: 'skill-up',
  contexts: [CONTEXTS.SKILL_UP_HOME, CONTEXTS.SKILL_UP_SESSION, CONTEXTS.SKILL_UP_COMPETENCES],

  centerView: {
    [CONTEXTS.SKILL_UP_HOME]: lazyView(() => import('./views/SkillUpHomeView'), 'SkillUpHomeView'),
    [CONTEXTS.SKILL_UP_SESSION]: lazyView(
      () => import('./views/LearningSessionView'),
      'LearningSessionView',
    ),
    [CONTEXTS.SKILL_UP_COMPETENCES]: lazyView(
      () => import('./views/SkillUpCompetencesView'),
      'SkillUpCompetencesView',
    ),
  },

  panels: {
    left: {
      tabs: {
        [CONTEXTS.SKILL_UP_HOME]: leftTabs,
        [CONTEXTS.SKILL_UP_SESSION]: leftTabs,
        [CONTEXTS.SKILL_UP_COMPETENCES]: leftTabs,
      },
    },
    right: {
      tabs: {
        // SKILL_UP_HOME / SKILL_UP_COMPETENCES : pas de sidebar droite.
        // Seul SKILL_UP_SESSION montre le Parcours (lié à une session active).
        [CONTEXTS.SKILL_UP_SESSION]: [
          {
            id: GRAPH_TAB_ID,
            label: 'Parcours',
            icon: GitFork,
            view: lazyView(() => import('./views/SkillUpGraphView'), 'SkillUpGraphView'),
          },
          {
            // Toutes les explications demandées pendant la session. S'ouvre de
            // lui-même quand l'apprenant en demande une (`PassageNode`).
            id: LEXIQUE_TAB_ID,
            label: 'Explications',
            icon: Lightbulb,
            view: lazyView(() => import('./views/SkillUpLexiqueView'), 'SkillUpLexiqueView'),
          },
        ],
      },
    },
  },

  panelRequirements: {
    [CONTEXTS.SKILL_UP_HOME]: {
      // Sur HOME : Sessions d'abord (entrée pour démarrer/reprendre).
      // Sur SESSION / COMPETENCES : Workspace d'abord (l'utilisateur bosse,
      // ses notes sont primaires comme un file explorer IDE).
      left: { open: true, defaultActiveTab: 'skill-up-sessions' },
      right: { open: false },
    },
    [CONTEXTS.SKILL_UP_SESSION]: {
      left: { open: true, defaultActiveTab: 'skill-up-workspace' },
      right: { open: true, defaultActiveTab: GRAPH_TAB_ID },
    },
    [CONTEXTS.SKILL_UP_COMPETENCES]: {
      left: { open: true, defaultActiveTab: 'skill-up-workspace' },
      right: { open: false },
    },
  },

  // Réactivée (SAAS-869) : l'écran de session se câble sur les routes du pacer,
  // la feature a besoin d'être atteignable pour être testée au clic.
  //
  // `order: -10` la place en tête du groupe `top` : le rail trie sur
  // `(order ?? 0)` et toutes les entrées historiques sont à 0, donc un ordre
  // négatif est le seul moyen de passer devant sans renuméroter les autres.
  navigation: [
    { id: 'skill-up', label: 'Skill-up', icon: GraduationCap, to: '/skill-up', order: -10 },
  ],

  routes: [{ routes: skillUpRoutes, errorFallbackLabel: 'Skill-up' }],

  // Masquée pour la mise en production : la feature n'est pas encore ouverte
  // aux utilisateurs. Le flag retire le lien du rail, la vue centrale et les
  // panneaux ; le code reste en place. Pour la voir : `VITE_FEATURE_FLAGS=skill-up`
  // au build, ou `useFeatureFlagsStore.getState().setFlag('skill-up', true)`
  // en console. Les routes restent déclarées (un flag n'est pas un guard).
  enabledWhen: { featureFlag: 'skill-up' },
};
